import { commitMutation, type Environment, fetchQuery, graphql } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { themeObjectToString, type ThemeState } from '@atlaskit/tokens';

import { updateThemeWithDefaultValues } from '../utils';

import type { workspaceThemeManagementGetWorkspaceThemePreferenceQuery } from './__generated__/workspaceThemeManagementGetWorkspaceThemePreferenceQuery.graphql';
import type { workspaceThemeManagementSetWorkspaceThemePreferenceMutation } from './__generated__/workspaceThemeManagementSetWorkspaceThemePreferenceMutation.graphql';

/**
 * Fetches the user's workspace-level theme preference using Relay
 * @param environment - The Relay environment
 * @param workspaceAri - The workspace ARI
 * @param accountId - The user's account ID
 * @returns The theme state object or undefined if not found
 */
export const getWorkspaceThemePreference = async (
	environment: Environment,
	workspaceAri: string,
	accountId: string,
): Promise<Partial<ThemeState> | undefined> => {
	if (!fg('shared-user-preferences')) {
		return undefined;
	}

	const response = await fetchQuery<workspaceThemeManagementGetWorkspaceThemePreferenceQuery>(
		environment,
		graphql`
			query workspaceThemeManagementGetWorkspaceThemePreferenceQuery(
				$workspaceAri: ID!
				$accountId: ID!
			) {
				settings_userPreferencesResolved(workspaceAri: $workspaceAri, accountId: $accountId) {
					theme
				}
			}
		`,
		{
			workspaceAri,
			accountId,
		},
	).toPromise();

	const themeString = response?.settings_userPreferencesResolved?.theme;

	if (!themeString) {
		return undefined;
	}

	return updateThemeWithDefaultValues(themeString);
};

/**
 * Sets the user's workspace-level theme preference using Relay
 * @param environment - The Relay environment
 * @param workspaceAri - The workspace ARI
 * @param accountId - The user's account ID
 * @param themeState - The theme state object to set
 * @returns A promise that resolves when the mutation completes
 */
export const updateWorkspaceThemePreference = async (
	environment: Environment,
	workspaceAri: string,
	accountId: string,
	themeState: Partial<ThemeState>,
): Promise<void> => {
	if (!fg('shared-user-preferences')) {
		return;
	}

	// Serialize theme object to string for GraphQL
	const themeString = themeObjectToString(themeState);

	return new Promise((resolve, reject) => {
		commitMutation<workspaceThemeManagementSetWorkspaceThemePreferenceMutation>(environment, {
			mutation: graphql`
				mutation workspaceThemeManagementSetWorkspaceThemePreferenceMutation(
					$workspaceAri: ID!
					$accountId: ID!
					$theme: String!
				) {
					settings_updateUserPreferencesWorkspace(
						workspaceAri: $workspaceAri
						accountId: $accountId
						theme: $theme
					) {
						theme
					}
				}
			`,
			variables: {
				workspaceAri,
				accountId,
				theme: themeString,
			},
			onCompleted: (_, errors) => {
				if (errors && errors.length > 0) {
					reject(new Error(errors.map((e) => e.message).join('; ')));
				} else {
					resolve();
				}
			},
			onError: (error) => {
				reject(error);
			},
		});
	});
};
